/*
  WreathQueen cart confirmation + live header badge.
  The theme header uses custom markup, so do not overwrite it with Dawn's
  stock cart-icon-bubble section markup. Cart count comes from /cart.js.
*/
(() => {
  const CART_URL = (window.routes && window.routes.cart_url) || '/cart';
  let toast;
  let dismissTimer;
  let lastRequestId = 0;

  const makeToast = () => {
    if (toast) return toast;
    toast = document.createElement('div');
    toast.id = 'wq-cart-toast';
    toast.className = 'wq-cart-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');
    toast.innerHTML = [
      '<span class="wq-cart-toast__check" aria-hidden="true">✓</span>',
      '<div class="wq-cart-toast__copy">',
      '<strong class="wq-cart-toast__heading">Added to cart</strong>',
      '<span class="wq-cart-toast__detail">Your item is in your cart.</span>',
      '</div>',
      '<a class="wq-cart-toast__link" href="/cart">View cart</a>',
      '<button class="wq-cart-toast__close" type="button" aria-label="Close notification">×</button>',
    ].join('');
    toast.querySelector('.wq-cart-toast__link').href = CART_URL;
    toast.querySelector('.wq-cart-toast__close').addEventListener('click', hideToast);
    document.body.appendChild(toast);
    return toast;
  };

  const hideToast = () => {
    if (!toast) return;
    toast.classList.remove('is-visible');
    if (dismissTimer) clearTimeout(dismissTimer);
  };

  const showToast = (state, message) => {
    const el = makeToast();
    const success = state === 'success';
    const pending = state === 'pending';
    el.classList.toggle('is-error', state === 'error');
    el.classList.toggle('is-pending', pending);
    el.setAttribute('role', state === 'error' ? 'alert' : 'status');
    el.querySelector('.wq-cart-toast__check').textContent =
      success ? '✓' : pending ? '…' : '!';
    el.querySelector('.wq-cart-toast__heading').textContent =
      success ? 'Added to cart' : pending ? 'Adding to cart…' : 'Unable to add to cart';
    el.querySelector('.wq-cart-toast__detail').textContent =
      success ? 'Your item is in your cart.' :
        pending ? 'Processing your selection.' : (String(message || 'Please try again.'));
    el.querySelector('.wq-cart-toast__link').hidden = !success;
    el.classList.add('is-visible');
    if (dismissTimer) clearTimeout(dismissTimer);
    // Pending is never labelled successful until Shopify confirms the add.
    // The timeout is only a fallback for an unusually delayed or interrupted request.
    dismissTimer = setTimeout(
      () => pending && el.classList.contains('is-pending')
        ? showToast('error', 'This is taking longer than expected. Please check your cart.')
        : hideToast(),
      pending ? 15000 : (success ? 4500 : 6500)
    );
  };

  const setHeaderCount = (count) => {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;
    const qty = Math.max(0, Math.trunc(Number(count) || 0));
    const label = icon.querySelector('.header__icon-label');
    if (label) label.textContent = `Cart(${qty})`;
    icon.setAttribute('aria-label', `Shopping cart, ${qty} ${qty === 1 ? 'item' : 'items'}`);

    let badge = icon.querySelector('.cart-count-bubble');
    if (qty === 0) {
      if (badge) badge.remove();
      return;
    }
    if (!badge) {
      badge = document.createElement('div');
      badge.className = 'cart-count-bubble';
      icon.appendChild(badge);
    }
    let visible = badge.querySelector('[aria-hidden="true"]');
    if (!visible) {
      visible = document.createElement('span');
      visible.setAttribute('aria-hidden', 'true');
      badge.appendChild(visible);
    }
    visible.textContent = qty < 100 ? String(qty) : '';
    let accessible = badge.querySelector('.visually-hidden');
    if (!accessible) {
      accessible = document.createElement('span');
      accessible.className = 'visually-hidden';
      badge.appendChild(accessible);
    }
    accessible.textContent = `${qty} items in cart`;
  };

  const syncHeaderCount = () => {
    const requestId = ++lastRequestId;
    fetch(`${CART_URL}.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Cart status: ${res.status}`);
        return res.json();
      })
      .then((cart) => {
        if (requestId === lastRequestId) setHeaderCount(cart.item_count);
      })
      .catch((err) => {
        console.warn('[WreathQueen] Cart count refresh unavailable:', err);
      });
  };

  // Shopify already returns the cart-icon-bubble section in the same add request.
  // Extract the real new cart total from that response; a second request is unnecessary.
  const syncCountFromResponse = (response) => {
    const html = response && response.sections && response.sections['cart-icon-bubble'];
    if (typeof html !== 'string' || !html) return false;
    const root = new DOMParser().parseFromString(html, 'text/html');
    const badge = root.querySelector('.cart-count-bubble');
    if (!badge) return false;
    const visible = badge.querySelector('[aria-hidden="true"]');
    const raw = visible && visible.textContent.trim()
      ? visible.textContent.trim()
      : (badge.querySelector('.visually-hidden')?.textContent || '').match(/[0-9]+/)?.[0];
    const count = Number(raw);
    if (!Number.isFinite(count) || count < 0) return false;
    ++lastRequestId; // Ignore an older /cart.js response that is still pending.
    setHeaderCount(count);
    return true;
  };

  function init() {
    window.WQCartFeedback = { enabled: true, sync: syncHeaderCount };

    // Show immediate processing feedback at form submission, not after the network.
    // A green success check appears only after Shopify accepts the item.
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!form || !form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
      const button = form.querySelector('button[name="add"]');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      showToast('pending');
    }, true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'product-form') showToast('success');
        if (!syncCountFromResponse(event?.cartData)) syncHeaderCount();
      });
      subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        if (event && event.source === 'product-form') {
          showToast('error', event.message || event.errors || 'Please try again.');
        }
      });
    }
    syncHeaderCount();
    window.addEventListener('pageshow', syncHeaderCount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
